import { useState, useContext, useEffect, useCallback, useRef } from 'react';
import { LoadingContext, useFeedbackContext } from '../context';
import {
  getLocationsVisitedLastDay,
  getLatestUnitDayDuration,
  getBatteryHistory,
  getTotalUsageTimeToday,
} from '../services/apiServices';
import usePolling from './usePolling';
import {
  ERROR_USAGE,
  ERROR_LOCATION,
  ERROR_WATER_CHANGE,
  ERROR_BATERY_LIFE,
} from '../constants/errorMessages';
import useFirstRender from './useFirstRender';
import { serviceLogger } from '../utils/serviceLogger';

const POLLING_INTERVAL = 300000; // 5 mins

const INITIAL_ERROR_STATE = {
  battery: false,
  usage: false,
  location: false,
  waterChange: false,
};

const INITIAL_STATE = {
  usageData: null,
  locationData: null,
  waterChangeData: null,
  batteryLifeData: null,
  requestTimestamp: null,
  errors: { ...INITIAL_ERROR_STATE },
  cardErrors: { ...INITIAL_ERROR_STATE },
};

/**
 * Custom hook that retrieves usage, location and water change duration data and battery life for a given unit using a polling mechanism.
 *
 * This hook:
 *  - Subscribes to the LoadingContext to manage the global loading indicator.
 *    Note: The loading indicator is only displayed during the initial data fetch, and not on subsequent updates.
 *  - Fetches data concurrently: total usage time (for the current day), location history, latest water change duration, and current battery life.
 *  - Uses the global FeedbackContext to display an error toast if fetching fails.
 *  - Re-fetches data every 5 minutes (300000 ms) via `usePolling`.
 *
 * @param {Object} selectedUnit - The currently selected unit object.
 *
 * @returns {Object} An object containing:
 *   @property {Object|null} usageData - Total usage time for the current day.
 *   @property {Object|null} locationData - Latest location record for the unit.
 *   @property {Object|null} waterChangeData - Latest unit day duration for water changes
 *   @property {Object} errors - Error states with properties `usage`, `location` and `waterChange` indicating errors.
 */
export default function useUnitDashboardData(selectedUnit) {
  const [state, setState] = useState(INITIAL_STATE);
  const [lastSelectedUnitId, setLastSelectedUnitId] = useState(null);
  const isMounted = useRef(false);

  const isFirstRender = useFirstRender();
  const { setIsLoading } = useContext(LoadingContext);
  const { showToast } = useFeedbackContext();

  const pushError = useCallback(
    (draft, key, status, { title, copy }) => {
      if (status === 404) {
        draft.cardErrors[key] = true;
      } else {
        draft.errors[key] = true;
        showToast({ variant: 'error', title, copy });
      }
    },
    [showToast],
  );

  // Main fetch
  const fetchAllData = useCallback(async () => {
    if (isFirstRender) setIsLoading(true);

    try {
      const [usage, location, waterChange, battery] = await Promise.allSettled([
        getTotalUsageTimeToday(selectedUnit.esp32_mac_address),
        getLocationsVisitedLastDay(selectedUnit.esp32_mac_address),
        getLatestUnitDayDuration(selectedUnit.esp32_mac_address),
        getBatteryHistory(selectedUnit.id, 1, 1),
      ]);

      const draft = {
        requestTimestamp: new Date(),
        errors: { ...INITIAL_ERROR_STATE },
        cardErrors: { ...INITIAL_ERROR_STATE },
      };

      // Usage
      if (usage.status === 'fulfilled') {
        draft.usageData = usage.value;
      } else {
        pushError(draft, 'usage', usage?.reason?.status, {
          title: 'Usage Data Error',
          copy: ERROR_USAGE,
        });
      }

      // Location
      if (location.status === 'fulfilled') {
        const locationsVisitied = location.value ?? {};
        draft.locationData = locationsVisitied ?? null;
      } else {
        pushError(draft, 'location', location?.reason?.status, {
          title: 'Location Data Error',
          copy: ERROR_LOCATION,
        });
      }

      // Water Change
      if (waterChange.status === 'fulfilled') {
        draft.waterChangeData = waterChange.value;
      } else {
        pushError(draft, 'waterChange', waterChange?.reason?.status, {
          title: 'Water Change Data Error',
          copy: ERROR_WATER_CHANGE,
        });
      }

      // Battery
      if (battery.status === 'fulfilled') {
        draft.batteryLifeData = battery.value;
      } else {
        pushError(draft, 'battery', battery?.reason?.status, {
          title: 'Battery Life Data Error',
          copy: ERROR_BATERY_LIFE,
        });
      }

      if (isMounted.current) {
        setState((prev) => ({ ...prev, ...draft }));
      }
    } catch (err) {
      serviceLogger.error('Unexpected error in fetchAllData:', err);
    } finally {
      if (isFirstRender) setIsLoading(false);
    }
  }, [selectedUnit?.id, selectedUnit?.esp32_mac_address, isFirstRender, setIsLoading, pushError]);

  useEffect(() => {
    isMounted.current = true;

    if (!selectedUnit?.id || selectedUnit?.id === lastSelectedUnitId) return;
    setLastSelectedUnitId(selectedUnit?.id);
    fetchAllData(); // initial load
    return () => {
      isMounted.current = false;
    };
  }, [fetchAllData, lastSelectedUnitId, selectedUnit.id]);

  usePolling(fetchAllData, POLLING_INTERVAL);

  return { ...state, fetchAllData };
}
